import { UseFormReturnType } from "@mantine/form";
import NumberInput from "src/components/NumberInput/NumberInput";
import TextInput from "src/components/TextInput/TextInput";
import Switch from "src/components/Switch/Switch";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import Select from "src/components/Select";
import DatePicker from "src/components/DatePicker";
import ActionCard from "src/components/ActionCard";
import {
  ExpirationMode,
  GetPackageQuery,
  ServiceFrequency,
  Unit,
  useGetLocationsQuery,
  useGetOrganizationServicesLazyQuery,
  useGetPrivateSessionDurationsNameQuery,
} from "src/generated/graphql";
import { AppContext } from "src/api/AppContext";
import { useContext, useEffect } from "react";
import SegmentedControl from "src/components/SegmentedControl";

interface ServiceAllotmentFormFieldsProps<T> {
  form: UseFormReturnType<T>;
  isActive?: boolean;
  isRecurring?: boolean;
  isGenericPackage?: boolean;
  packageLocations?: NonNullable<GetPackageQuery["packageOne"]>["locations"];
  isPackage?: boolean;
}

const ServiceAllotmentFormFields = <T,>({
  form,
  isActive = false,
  isRecurring = false,
  isGenericPackage,
  packageLocations,
  isPackage,
}: ServiceAllotmentFormFieldsProps<T>) => {
  const { currentOrganization } = useContext(AppContext);
  const { data: locations } = useGetLocationsQuery();
  const [getServices, { data: services }] =
    useGetOrganizationServicesLazyQuery();

  const { data: sessions } = useGetPrivateSessionDurationsNameQuery();

  const { value: switchValue, ...switchProps } =
    form.getInputProps("startMode");
  const { value: expiredValue, ...expirationProps } =
    form.getInputProps("mode");

  const isPrivateSession = form.getInputProps("isPrivateSession").value;

  useEffect(() => {
    if (currentOrganization?.id) {
      getServices({
        variables: { orgId: currentOrganization?.id || "" },
      });
    }
  }, [currentOrganization]);

  return (
    <>
      <ActionCard title="General" mb={10}>
        <TextInput
          label="Name"
          {...form.getInputProps("name")}
          disabled={isActive}
        />
        <Textarea
          label="Description"
          disabled={isActive}
          {...form.getInputProps("description")}
        />

        <Switch
          mt={15}
          labelPosition="left"
          disabled={isActive}
          label="Private Session"
          checked={isPrivateSession}
          {...form.getInputProps("isPrivateSession")}
        />

        {isPrivateSession ? (
          <MultiSelect
            disabled={isActive}
            label="Private Sessions"
            data={
              sessions?.privateSessionDurationMany?.map((session) => ({
                value: session.id,
                label: `${session.session.name} / ${session.duration} mins`,
              })) || []
            }
            {...form.getInputProps("privateSessions")}
          />
        ) : (
          <MultiSelect
            disabled={isActive}
            label="Services"
            data={
              services?.organizationServiceMany?.map((service) => ({
                value: service.id,
                label: service.name,
              })) || []
            }
            {...form.getInputProps("services")}
          />
        )}

        <MultiSelect
          disabled={isActive}
          label="Locations"
          data={
            isPackage && !isGenericPackage
              ? packageLocations?.map((e) => ({
                  value: e.id,
                  label: e.name,
                })) || []
              : locations?.locationMany.map((location) => ({
                  value: location.id,
                  label: location.name,
                })) || []
          }
          {...form.getInputProps("locations")}
        />
      </ActionCard>

      <ActionCard title="Credits" mb={10}>
        <NumberInput
          label="Daily Limit"
          disabled={isActive}
          {...form.getInputProps("dailyLimit")}
        />
        <NumberInput
          label="Credits"
          disabled={isActive}
          {...form.getInputProps("credits")}
        />
        {isRecurring && (
          <Select
            withAsterisk
            data={
              Object.keys(ServiceFrequency)?.map((val) => ({
                value: val,
                label: val,
              })) || []
            }
            label="Frequency"
            {...form.getInputProps("serviceFrequency")}
          />
        )}

        <Switch
          mt={15}
          labelPosition="left"
          disabled={isActive}
          label="Schedule First Allocation"
          checked={switchValue} // startMode enum
          {...switchProps}
        />
        {switchValue && (
          <DatePicker
            label="First Allocation On"
            {...form.getInputProps("startAt")}
            disabled={isActive}
            dropdownPosition="top-start"
          />
        )}
      </ActionCard>

      <ActionCard
        title="Expiration"
        hideChild={expiredValue === ExpirationMode.Never ? true : false}
        rightIconButton={
          <SegmentedControl
            data={[
              ExpirationMode.Never,
              ExpirationMode.Date,
              ExpirationMode.Duration,
            ]}
            value={expiredValue}
            {...expirationProps}
          />
        }
      >
        {expiredValue === ExpirationMode.Date && (
          <DatePicker
            label="Date of Expiration"
            disabled={isActive}
            dropdownPosition="top-start"
            {...form.getInputProps("expiresAt")}
          />
        )}
        {expiredValue === ExpirationMode.Duration && (
          <>
            <Select
              disabled={isActive}
              data={Object.keys(Unit)}
              label="Unit"
              {...form.getInputProps("expirationUnit")}
            />
            <NumberInput
              label="Value"
              disabled={isActive}
              {...form.getInputProps("expirationValue")}
            />
          </>
        )}
      </ActionCard>
    </>
  );
};

export default ServiceAllotmentFormFields;
